// API Configuration for Go Backend Integration
const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1',
  TIMEOUT: 10000,
  RETRY_ATTEMPTS: 3,
};

// Log API configuration for debugging (only in development)
if (import.meta.env.DEV) {
}

// API Client with interceptors and error handling
class APIClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  // Generic request method with error handling and retry logic
  async request(endpoint, options = {}, retryCount = 0) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      timeout: this.timeout,
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Check if response has content before parsing JSON
      const contentType = response.headers.get('content-type');
      const hasJsonContent = contentType && contentType.includes('application/json');

      let data = null;
      if (hasJsonContent) {
        try {
          data = await response.json();
        } catch (e) {
          // JSON parse error - response body might be empty
          data = null;
        }
      }

      if (!response.ok) {
        // Extract error message from backend response. Prefer `error` over
        // `message` when it's a plain string: many handlers send a generic
        // wrapper message (e.g. "Failed to release escrow") alongside the
        // actually-informative reason in `error` (e.g. "the seller must mark
        // this order as delivered before you can confirm delivery") — the
        // old `message`-first order silently discarded that detail. Some
        // endpoints (e.g. validation errors) put a {field: reason} object in
        // `error` instead of a string, so that case still falls back to
        // `message` rather than stringifying the object.
        const errorMessage =
          (typeof data?.error === 'string' && data.error) ||
          data?.message ||
          `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        error.endpoint = endpoint; // Store endpoint for context-aware error handling
        throw error;
      }

      return data;
    } catch (error) {
      error.endpoint = error.endpoint || endpoint; // Ensure endpoint is set

      const handledError = this.handleError(error);

      // Check if we should retry with refresh token
      if (handledError.shouldRetryWithRefresh && retryCount === 0) {
        try {
          await this.refreshAccessToken();
          // Retry the original request with new token (only once)
          return await this.request(endpoint, options, retryCount + 1);
        } catch (refreshError) {
          // Refresh failed, throw the original error
          throw handledError;
        }
      }

      throw handledError;
    }
  }

  // Get authentication headers (JWT token from localStorage)
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Refresh access token using refresh token
  async refreshAccessToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      const newToken = data.data?.token || data.token;
      const newRefreshToken = data.data?.refresh_token || data.refresh_token;

      if (newToken) {
        localStorage.setItem('authToken', newToken);
      }
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      return newToken;
    } catch (error) {
      // Refresh failed - logout user
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      throw error;
    }
  }

  // Error handler for consistent error formatting
  handleError(error) {
    if (error.name === 'AbortError') {
      return new Error('Request timeout');
    }

    if (error.message.includes('404') || error.message.includes('Endpoint not found')) {
      return new Error('Endpoint not found');
    }

    if (error.message.includes('301') || error.message.includes('Moved Permanently')) {
      return new Error('Endpoint has been moved or is unavailable');
    }

    // Check for 401 errors
    if (error.message.includes('401') || error.message.includes('Invalid or expired token') || error.status === 401) {
      // IMPORTANT: Don't treat login/register failures as session expiry
      const isAuthEndpoint = error.endpoint && (
        error.endpoint.includes('/auth/login') ||
        error.endpoint.includes('/auth/register') ||
        error.endpoint.includes('/auth/seller-login') ||
        error.endpoint.includes('/auth/seller-register') ||
        error.endpoint.includes('/auth/refresh')
      );

      // If it's a login/register/refresh endpoint, return the original error message
      if (isAuthEndpoint) {
        return error; // Return original error (e.g., "Invalid email or password")
      }

      // Check if user is already logged out (no token in localStorage)
      const hasToken = localStorage.getItem('authToken');
      const hasRefreshToken = localStorage.getItem('refreshToken');

      // If there's no token, user is already logged out - don't show alert or redirect
      if (!hasToken) {
        const authError = new Error('Authorization token required');
        authError.isAuthError = true;
        authError.status = 401;
        authError.silent = true; // Flag to indicate this should be handled silently
        return authError;
      }

      // Try to refresh token if refresh token exists
      if (hasRefreshToken) {
        error.shouldRetryWithRefresh = true; // Flag to indicate retry with refresh
        return error;
      }

      // For other endpoints with valid token but no refresh token, it's a session expiry issue
      const authError = new Error('Your session has expired. Please log in again.');
      authError.isAuthError = true;
      authError.status = 401;

      // Auto-logout: clear auth data and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Prevent multiple alerts/redirects by checking if we're already redirecting
      const isRedirecting = sessionStorage.getItem('isRedirecting');
      if (!isRedirecting) {
        sessionStorage.setItem('isRedirecting', 'true');

        // Show alert before redirect
        setTimeout(() => {
          alert('Your session has expired. Please log in again.');
          window.location.href = '/auth';
          // Clear flag after redirect starts
          setTimeout(() => sessionStorage.removeItem('isRedirecting'), 1000);
        }, 100);
      }

      return authError;
    }

    // fetch() rejects with a TypeError for any network-level failure (DNS,
    // connection refused/reset, TLS, offline) — the message text is
    // browser-specific and not part of the spec: Chrome/Edge say "Failed to
    // fetch", Firefox says "NetworkError when attempting to fetch resource.",
    // and Safari/WebKit (which every iOS browser is required to use, so this
    // covers iOS Chrome/Firefox too) says "Load failed". Matching on the
    // error's type instead of Chrome's specific wording catches all of them,
    // so a transient network blip shows the same friendly message on every
    // browser instead of leaking a raw, confusing string like "Load failed"
    // on Safari/iOS.
    if (error instanceof TypeError) {
      return new Error('Network error - unable to connect to server. Please check your connection and try again.');
    }

    return error;
  }

  // HTTP Methods
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url);
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }
}

// Create API client instance
const apiClient = new APIClient();

// Products API Service
export const productsAPI = {
  // Get all products with filters and pagination
  getAll: (params = {}) => apiClient.get('/products/', params),

  // Get featured/trending products for homepage
  // includSellerPrice: when true, backend returns seller_price field (for sellers only)
  getFeatured: (params = {}) => apiClient.get('/products/', { featured: true, ...params }),

  // Real products the admin flagged as a Flash Deal (Admin -> Products ->
  // Flash Deal), not the sitewide promo campaigns under Admin -> Marketing.
  // Premium members see a deal from the moment it's set; everyone else only
  // after its early-access window passes — resolved server-side from the
  // caller's own auth token, so no client-side premium check is needed here.
  getFlashDeals: (params = {}) => apiClient.get('/products/', { flash_deal: true, ...params }),

  // Get product by ID
  getById: (id) => apiClient.get(`/products/${id}`),

  // Valid color options for a given device model (model-specific, not a
  // generic list) — powers the listing form's Color dropdown.
  getDeviceColors: (model) => apiClient.get('/products/device-colors', { model }),

  // Search products
  search: (query, filters = {}) => apiClient.get('/products/', {
    search: query,
    ...filters
  }),

  // Get products by category
  getByCategory: (categoryId, params = {}) => apiClient.get('/products/', { category_id: categoryId, ...params }),

  // Add to wishlist
  addToWishlist: (productId) => apiClient.post('/wishlist/', { productId }),

  // Remove from wishlist
  removeFromWishlist: (itemId) => apiClient.delete(`/wishlist/${itemId}`),
};

// Categories API Service
export const categoriesAPI = {
  // Get all categories
  getAll: () => apiClient.get('/categories/'),

  // Get category by ID
  getById: (id) => apiClient.get(`/categories/${id}`),

  // Get category with products
  getWithProducts: (id, params = {}) => apiClient.get('/products/', { category_id: id, ...params }),
};

// Cart API Service
export const cartAPI = {
  // Get cart items
  getItems: () => apiClient.get('/cart/'),

  // Alias for getItems
  getCart: () => apiClient.get('/cart/'),

  // Add item to cart
  addItem: (productId, quantity = 1) => apiClient.post('/cart/add', {
    product_id: productId,
    quantity,
  }),

  // Update cart item quantity
  updateItem: (itemId, quantity) => apiClient.put('/cart/update', {
    item_id: itemId,
    quantity,
  }),

  // Remove item from cart
  removeItem: (itemId) => apiClient.delete(`/cart/remove/${itemId}`),

  // Clear cart
  clear: () => apiClient.delete('/cart/clear'),

};

// Wishlist API Service (matching mobile: apiService.ts lines 197-213)
export const wishlistAPI = {
  // Get wishlist items
  getWishlist: () => apiClient.get('/wishlist/'),

  // Add product to wishlist
  addToWishlist: (productId) => apiClient.post('/wishlist/', {
    product_id: productId
  }),

  // Remove product from wishlist
  removeFromWishlist: (itemId) => apiClient.delete(`/wishlist/${itemId}`),

  // Move wishlist item to cart
  moveToCart: (itemId) => apiClient.post(`/wishlist/${itemId}/move-to-cart`),

  // Share wishlist
  shareWishlist: () => apiClient.post('/wishlist/share'),

};

// User API Service (matching mobile app implementation)
export const userAPI = {
  // Get user profile
  getProfile: () => apiClient.get('/user/profile'),

  // Update user profile
  updateProfile: (data) => apiClient.put('/user/profile', data),

  // Get user orders
  getOrders: (params = {}) => apiClient.get('/user/orders/', params),

  // Get user wishlist
  getWishlist: () => apiClient.get('/wishlist/'),

  // Get user addresses (matching mobile app)
  getAddresses: () => apiClient.get('/user/addresses/'),
  getUserAddresses: () => apiClient.get('/user/addresses/'), // Alias for mobile app compatibility

  // Add user address
  addAddress: (addressData) => apiClient.post('/user/addresses', addressData),

  // Update user address
  updateAddress: (addressId, addressData) => apiClient.put(`/user/addresses/${addressId}`, addressData),

  // Delete user address
  deleteAddress: (addressId) => apiClient.delete(`/user/addresses/${addressId}`),

  // Set default address
  setDefaultAddress: (addressId) => apiClient.put(`/user/addresses/${addressId}/default`),

  // Create address (alias for addAddress for consistency with mobile app)
  createAddress: (addressData) => apiClient.post('/user/addresses', addressData),

  // Change password
  changePassword: (passwordData) => apiClient.post('/user/change-password', passwordData),

  // Get wallet balance (alias for wallet API)
  getWalletBalance: () => apiClient.get('/wallet/balance'),

  // Get wallet transactions (alias for wallet API)
  getWalletTransactions: (params = {}) => apiClient.get('/wallet/transactions', params),

  // ===== BANK ACCOUNT MANAGEMENT =====
  // Get user bank accounts
  getBankAccounts: () => apiClient.get('/user/bank-accounts/'),

  // Get full Nigerian bank list (name + code) from Monnify
  getBanks: () => apiClient.get('/banks'),

  // Verify bank account with Monnify
  verifyBankAccount: (accountNumber, bankCode) =>
    apiClient.post('/user/bank-accounts/verify', {
      account_number: accountNumber,
      bank_code: bankCode
    }),

  // Add new bank account
  addBankAccount: (bankData) => apiClient.post('/user/bank-accounts', bankData),

  // Set default bank account
  setDefaultBankAccount: (bankAccountId) => apiClient.put(`/user/bank-accounts/${bankAccountId}/default`),

  // Delete bank account
  deleteBankAccount: (bankAccountId) => apiClient.delete(`/user/bank-accounts/${bankAccountId}`),


  // ===== TWO-FACTOR AUTHENTICATION (2FA) =====
  // Get 2FA status
  get2FAStatus: () => apiClient.get('/user/2fa/status'),

  // Enable 2FA (initiates OTP generation)
  enable2FA: () => apiClient.post('/user/2fa/enable'),

  // Verify 2FA with OTP (completes 2FA setup)
  verify2FA: (otp) => apiClient.post('/user/2fa/verify', { otp }),

  // Disable 2FA (requires password confirmation)
  disable2FA: (password) => apiClient.post('/user/2fa/disable', { password }),

  // Generate backup codes
  generateBackupCodes: () => apiClient.post('/user/2fa/backup-codes'),

  // ===== ACTIVE SESSION MANAGEMENT =====
  // Get all active login sessions for the current user
  getActiveSessions: () => apiClient.get('/user/sessions/'),

  // Terminate a specific session by ID
  terminateSession: (sessionId) => apiClient.delete(`/user/sessions/${sessionId}`),

  // Terminate all sessions except the current one
  terminateAllOtherSessions: () => apiClient.delete('/user/sessions/all'),

  // Push notifications
  updatePushToken: (token) => apiClient.post('/user/push-token', { token }),

  // Account deletion
  deleteAccount: (password) => apiClient.delete('/user/account', { password }),

  // Data management
  exportUserData: () => apiClient.post('/user/export-data')
};

// --- Want to Buy (WTB) Request API ---
export const requestAPI = {
  // Create a new WTB request
  createRequest: (data) => apiClient.post('/requests', data),

  // Get all open WTB requests (with optional filters). Sellers/admins only —
  // buyer requests are personal, backend returns 403 for buyer accounts.
  getRequests: (params = {}) => apiClient.get('/requests/', params),

  // Get requests created by the current user
  getMyRequests: () => apiClient.get('/requests/my'),

  // Get a single request details
  getRequest: (id) => apiClient.get(`/requests/${id}`),

  // Respond to a request (Seller only)
  respondToRequest: (id, data) => apiClient.post(`/requests/${id}/respond`, data),

  // Get all responses for a specific request
  getRequestResponses: (id) => apiClient.get(`/requests/${id}/responses`),


  // Delete a request
  deleteRequest: (id) => apiClient.delete(`/requests/${id}`)
};

// Authentication API Service
export const authAPI = {
  // User login
  login: (credentials) => apiClient.post('/auth/login', credentials),

  // Google login
  googleLogin: (credential) => apiClient.post('/auth/google-login', { credential }),

  // User registration
  register: (userData) => apiClient.post('/auth/register', userData),

  // User logout
  logout: () => apiClient.post('/user/logout'),


  // Request a password reset link
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),

  // Set a new password using the token from the reset email
  resetPassword: (token, newPassword) =>
    apiClient.post('/auth/reset-password', { token, new_password: newPassword }),

  // Resend email verification
  resendEmailVerification: (email) => apiClient.post('/auth/resend-email-verification', { email }),

  // Verify email with token
  verifyEmail: (token) => apiClient.get(`/auth/verify-email?token=${token}`),

  // Verify phone
  verifyPhone: (phoneData) => apiClient.post('/user/verify-phone', phoneData),

  // Public bank account verification (for seller registration - no auth required)
  verifyBankAccount: (accountNumber, bankCode) =>
    apiClient.post('/bank-accounts/verify', {
      account_number: accountNumber,
      bank_code: bankCode
    }),
};

// Orders API Service (matching mobile app implementation)
export const ordersAPI = {
  // Create order
  create: (orderData) => apiClient.post('/orders', orderData),
  createOrder: (orderData) => apiClient.post('/orders', orderData), // Alias

  // Get order by ID
  getById: (orderId) => apiClient.get(`/user/orders/${orderId}/`),
  getOrder: (orderId) => apiClient.get(`/user/orders/${orderId}/`), // Alias

  // Get user orders
  getUserOrders: (params = {}) => apiClient.get('/user/orders/', params),

  // Cancel order
  cancel: (orderId) => apiClient.post(`/user/orders/${orderId}/cancel`),
  cancelOrder: (orderId) => apiClient.post(`/user/orders/${orderId}/cancel`), // Alias

  // Track order
  track: (orderId) => apiClient.get(`/orders/${orderId}/track`),

  // Get delivery codes for order (pickup and delivery verification codes)
  getDeliveryCodes: (orderId) => apiClient.get(`/user/orders/${orderId}/delivery-codes`),

  // Verify payment for order
  verifyPayment: (reference) => apiClient.get(`/payment/verify/${reference}`),

  // Confirm delivery (buyer confirms receipt)
  confirmDelivery: (orderId) => apiClient.post(`/orders/${orderId}/confirm-delivery`),

  // Get order details
  getDetails: (orderId) => apiClient.get(`/user/orders/${orderId}`),

  // Get order with items
  getWithItems: (orderId) => apiClient.get(`/user/orders/${orderId}`),

  // Return order
  requestReturn: (orderId, reason) => apiClient.post(`/user/orders/${orderId}/return`, { reason }),

};

// Seller API Service
export const sellerAPI = {
  // Get seller dashboard data
  getDashboard: () => apiClient.get('/seller/dashboard'),

  // Get seller sales analytics
  getSalesAnalytics: () => apiClient.get('/seller/analytics/sales'),

  // Get seller product analytics
  getProductAnalytics: () => apiClient.get('/seller/analytics/products'),

  // Get seller revenue analytics
  getRevenueAnalytics: () => apiClient.get('/seller/analytics/revenue'),

  // Get seller profile
  getProfile: () => apiClient.get('/seller/profile'),

  // Update seller profile
  updateProfile: (profileData) => apiClient.put('/seller/profile', profileData),

  // Get seller products
  getProducts: (params = {}) => apiClient.get('/seller/products/', params),

  // Add seller product
  addProduct: (productData) => apiClient.post('/seller/products', productData),

  // Update seller product
  updateProduct: (productId, productData) => apiClient.put(`/seller/products/${productId}`, productData),

  // Delete seller product
  deleteProduct: (productId) => apiClient.delete(`/seller/products/${productId}`),

  // Get seller orders
  getOrders: (params = {}) => apiClient.get('/seller/orders/', params),

  // Get seller order by ID
  getOrder: (orderId) => apiClient.get(`/seller/orders/${orderId}/`),

  // Update order status
  updateOrderStatus: (orderId, status) => apiClient.put(`/seller/orders/${orderId}/status`, { status }),

  // Ship an order — backend: POST /seller/orders/:id/ship
  shipOrder: (orderId, trackingData) => apiClient.post(`/seller/orders/${orderId}/ship`, trackingData),

  // Mark order as delivered — backend: POST /seller/orders/:id/mark-delivered
  markOrderDelivered: (orderId) => apiClient.post(`/seller/orders/${orderId}/mark-delivered`),

  // Reject an order — backend: POST /seller/orders/:id/reject
  rejectOrder: (orderId, reason) => apiClient.post(`/seller/orders/${orderId}/reject`, { reason }),

  // Submit IMEI(s) for one order item, one entry per physical unit
  // (matching that item's quantity). Write-once — never returned back.
  submitOrderItemIMEI: (orderId, itemId, imeis) =>
    apiClient.post(`/seller/orders/${orderId}/items/${itemId}/imei`, { imeis }),

  // Upload a product image — backend: POST /seller/upload-image
  uploadProductImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.request('/seller/upload-image', {
      method: 'POST',
      body: formData,
      headers: { ...apiClient.getAuthHeaders() }, // no Content-Type so browser sets multipart boundary
    });
  },
};

// Buyer API Service
export const buyerAPI = {
  // Get buyer dashboard overview
  getDashboard: () => apiClient.get('/buyer/dashboard'),

  // Get buyer recent activity
  getRecentActivity: () => apiClient.get('/buyer/recent-activity'),

  // Get buyer analytics
  getAnalytics: () => apiClient.get('/analytics/buyer'),
};

// Notifications API Service
export const notificationsAPI = {
  // Get user notifications (trailing slash matches the registered backend route)
  getNotifications: () => apiClient.get('/notifications/'),

  // Mark notification as read
  markAsRead: (notificationId) => apiClient.put(`/notifications/${notificationId}/read`),

  // Delete notification
  delete: (notificationId) => apiClient.delete(`/notifications/${notificationId}`),

  // Get notification preferences
  getPreferences: () => apiClient.get('/notifications/preferences'),

  // Update notification preferences — backend: POST /notifications/preferences
  updatePreferences: (preferences) => apiClient.post('/notifications/preferences', preferences),

  // Mark all as read — backend: PUT /notifications/read-all
  markAllAsRead: () => apiClient.put('/notifications/read-all'),
};

// Activity API Service
export const activityAPI = {
  // Get user activity
  getUserActivity: () => apiClient.get('/user/activity-log'),
};

// Wallet API Service (matching mobile app implementation)
export const walletAPI = {
  // Get wallet balance
  getBalance: () => apiClient.get('/wallet/balance'),

  // Get wallet transactions history with pagination and filters
  getTransactions: (params = {}) => apiClient.get('/wallet/transactions', params),


  // Request withdrawal
  requestWithdrawal: (withdrawalData) => apiClient.post('/wallet/withdraw', withdrawalData),

  // Get wallet analytics (for wallet screen)
  getAnalytics: () => apiClient.get('/wallet/analytic'),
};

// Payment API Service (matching mobile app implementation)
export const paymentAPI = {
  // Initialize payment (integrated with order creation)
  initialize: (paymentData) => apiClient.post('/orders/', paymentData),

  // Verify payment status
  verify: (orderId) => apiClient.get(`/orders/${orderId}/track/`),

  // Initialize Monnify payment for subscription
  initializePayment: (paymentData) => apiClient.post('/payment/initialize', paymentData),

  // Initialize wallet topup payment
  initializeWalletTopup: (amount, callbackUrl) => apiClient.post('/payment/initialize', {
    amount,
    payment_type: 'wallet_topup',
    currency: 'NGN',
    callback_url: callbackUrl
  }),

  // Verify Monnify payment
  verifyPayment: (reference) => apiClient.get(`/payment/verify/${reference}`),



  // Check wallet balance before payment
  checkWalletBalance: () => apiClient.get('/wallet/balance'),

  // Process swap payment
  processSwapPayment: (swapId, paymentData) =>
    apiClient.post(`/swap/${swapId}/payment`, paymentData),
};

// Subscription API Service
export const subscriptionAPI = {
  // Get subscription plans
  getPlans: () => apiClient.get('/subscription/plans'),

  // Get premium features
  getFeatures: () => apiClient.get('/subscription/features'),

  // Get current subscription status
  getStatus: () => apiClient.get('/subscription/status'),

  // Create new subscription
  create: (planId) => apiClient.post('/subscription/create', { plan_id: planId }),

  // Subscribe to a plan
  subscribe: (planId, paymentReference) => apiClient.post('/subscription/subscribe', {
    plan_id: planId,
    payment_reference: paymentReference
  }),

  // Cancel subscription
  cancel: () => apiClient.post('/subscription/cancel'),

  // Upgrade subscription
  upgrade: (planId) => apiClient.post('/subscription/upgrade', { plan_id: planId }),

  // Get billing history
  getBillingHistory: () => apiClient.get('/subscription/billing-history'),
};

// Chat/Messages API Service (matching mobile app implementation)
export const chatAPI = {
  // Get all conversations for current user
  getConversations: async () => {
    try {
      return await apiClient.get('/conversations/');
    } catch (error) {
      return { data: [] };
    }
  },

  // Create new conversation with another user (matching mobile app)
  createConversation: (participantId, productId = null, type = 'direct') =>
    apiClient.post('/conversations/', {
      participantId: participantId, // Mobile app uses camelCase
      participant_id: participantId, // Backend expects snake_case
      product_id: productId,
      type: type
    }),

  // Get messages in a conversation
  getMessages: (conversationId, page = 1) => {
    const params = page > 1 ? `?page=${page}` : '';
    return apiClient.get(`/conversations/${conversationId}/messages${params}`);
  },

  // Send a message in a conversation
  sendMessage: (conversationId, content, type = 'text', metadata = null) =>
    apiClient.post('/messages/', {
      conversation_id: conversationId,
      content: content,
      type: type,
      metadata: metadata
    }),

  // Mark messages as read (PUT method as per backend)
  markAsRead: (conversationId) =>
    apiClient.put(`/conversations/${conversationId}/read`),

  // Legacy method name for backwards compatibility
  markMessagesAsRead: (conversationId) =>
    apiClient.put(`/conversations/${conversationId}/read`),

  // Get conversation details
  getConversation: (conversationId) =>
    apiClient.get(`/conversations/${conversationId}`),

  // Search users for starting new conversations
  searchUsers: (query) =>
    apiClient.get(`/users/search?q=${encodeURIComponent(query)}`),

  // Get recent chat users
  getRecentChatUsers: () =>
    apiClient.get('/conversations/recent-users'),

  // Archive conversation
  archiveConversation: (conversationId) =>
    apiClient.put(`/conversations/${conversationId}/archive`),

  // Mute/unmute conversation
  muteConversation: (conversationId, muted = true) =>
    apiClient.put(`/conversations/${conversationId}/mute`, { muted }),

  // Delete conversation
  deleteConversation: (conversationId) =>
    apiClient.delete(`/conversations/${conversationId}`),



  // Share product in chat
  shareProduct: (conversationId, productId) =>
    apiClient.post('/messages', {
      conversation_id: conversationId,
      content: productId,
      type: 'product'
    }),


  // Block a conversation — backend: PUT /conversations/:id/block
  blockConversation: (conversationId) =>
    apiClient.put(`/conversations/${conversationId}/block`),

  // Search messages — backend: GET /conversations/search
  searchMessages: (query) =>
    apiClient.get('/conversations/search', { q: query }),
};

// Escrow API — order checkouts flagged use_escrow route through Pandascrow
// (real third-party fund holding); confirmDelivery takes an OTP for those.
// requestEscrowRefund opens a Pandascrow dispute for that provider instead of
// an instant refund — the backend decides based on the order's escrow record.
export const escrowAPI = {
  getEscrowStatus: (orderId) => apiClient.get(`/escrow/order/${orderId}`).catch(() => ({
    data: { success: true, message: 'Escrow status retrieved', data: null }
  })),
  getEscrowTimeline: (orderId) => apiClient.get(`/escrow/timeline/${orderId}`).catch(() => ({
    data: { success: true, message: 'Escrow timeline retrieved', data: [] }
  })),
  confirmDelivery: (orderId, otp) => apiClient.post(`/orders/${orderId}/confirm-delivery`, otp ? { otp } : {}),
  requestEscrowRefund: (orderId, reason) => apiClient.post(`/orders/${orderId}/request-refund`, { reason }),
  updateNotificationPreferences: (preferences) => apiClient.post('/notifications/preferences', preferences),
  getAll: (params = {}) => apiClient.get('/admin/escrows', params),
  getDetails: (escrowId) => apiClient.get(`/admin/escrows/${escrowId}`),
  releaseEscrow: (escrowId, reason) => apiClient.post(`/admin/escrows/${escrowId}/release`, { reason }),
  disputeEscrow: (escrowId, reason) => apiClient.post(`/admin/escrows/${escrowId}/refund`, { reason }),
  getAnalytics: () => apiClient.get('/admin/escrows/analytics'),
  getSettings: () => apiClient.get('/admin/escrows/settings'),
  updateSettings: (settings) => apiClient.put('/admin/escrows/settings', settings),
};

// Badge and Rewards API Service (matching backend: routes/routes.go lines 651-653, 668-669)
export const badgesAPI = {
  // Get user's earned badges (routes: /user/badges or /badges/)
  getUserBadges: () => apiClient.get('/user/badges'),

  // Get all available badges
  getAvailableBadges: () => apiClient.get('/badges/available'),

  // Get user's reward points and level (route: /user/rewards)
  getUserRewards: () => apiClient.get('/user/rewards'),

  // Get rewards transaction history (route: /user/rewards/history)
  getRewardsHistory: () => apiClient.get('/user/rewards/history'),
};

// Reports API Service (matching backend: handlers/reports.go and unified ticket system)
export const reportsAPI = {
  // Report a product for violations
  reportProduct: (productId, reason, details) => apiClient.post('/reports/product', {
    product_id: productId,
    reason: reason,
    details: details
  }),

  // Report a user for violations
  reportUser: (userId, reason, details) => apiClient.post('/reports/user', {
    reported_user_id: userId,
    reason: reason,
    details: details
  }),

  // Submit a bug report using unified ticket system (routes: /support/tickets or /tickets)
  submitBugReport: (bugData) => apiClient.post('/support/tickets', {
    type: 'bug_report',
    subject: bugData.title,
    description: `**Bug Type:** ${bugData.type}\n\n**Description:**\n${bugData.description}\n\n${bugData.stepsToReproduce ? `**Steps to Reproduce:**\n${bugData.stepsToReproduce}\n\n` : ''}${bugData.expectedBehavior ? `**Expected Behavior:**\n${bugData.expectedBehavior}\n\n` : ''}${bugData.actualBehavior ? `**Actual Behavior:**\n${bugData.actualBehavior}\n\n` : ''}**Priority:** ${bugData.priority}`,
    priority: bugData.priority,
    category: bugData.type,
    metadata: {
      bug_type: bugData.type,
      steps_to_reproduce: bugData.stepsToReproduce,
      expected_behavior: bugData.expectedBehavior,
      actual_behavior: bugData.actualBehavior,
      screenshots: bugData.screenshots
    }
  }),
};

// Reviews API Service (matching backend: handlers/review.go)
export const reviewsAPI = {
  // Create a new review
  createReview: (data) => apiClient.post('/reviews', {
    product_id: data.productId,
    order_id: data.orderId,
    rating: data.rating,
    title: data.title || '',
    comment: data.comment,
    images: data.images || []
  }),

  // Get reviews for a product — backend: GET /products/:id/reviews
  getProductReviews: (productId, page = 1, limit = 10) =>
    apiClient.get(`/products/${productId}/reviews`, { page, limit }),

  // Get user's own reviews — backend: GET /reviews/my-reviews
  getUserReviews: (page = 1, limit = 10) =>
    apiClient.get('/reviews/my-reviews', { page, limit }),

  // Update a review
  updateReview: (reviewId, data) => apiClient.put(`/reviews/${reviewId}`, {
    rating: data.rating,
    title: data.title || '',
    comment: data.comment,
    images: data.images || []
  }),

  // Delete a review
  deleteReview: (reviewId) => apiClient.delete(`/reviews/${reviewId}`),

  // Get product rating statistics — backend: GET /products/:id/rating-stats
  getProductRatingStats: (productId) => apiClient.get(`/products/${productId}/rating-stats`),

  // Vote review as helpful
  voteReview: (reviewId, helpful) => apiClient.post(`/reviews/${reviewId}/vote`, { helpful }),

  // Seller: submit a response to a review — backend: POST /reviews/:id/response (NEW)
  submitSellerResponse: (reviewId, response) =>
    apiClient.post(`/reviews/${reviewId}/response`, { response }),

  // Get reviews for a seller's product — backend: GET /reviews/seller/:id
  getSellerReviews: (sellerId, params = {}) =>
    apiClient.get(`/reviews/seller/${sellerId}`, params),

  // Create a seller review (review of a seller) — backend: POST /reviews/seller
  createSellerReview: (data) => apiClient.post('/reviews/seller', {
    seller_id: data.sellerId,
    order_id: data.orderId,
    rating: data.rating,
    comment: data.comment,
  }),

  // Get reviews by seller on public profile — backend: GET /sellers/:id/reviews
  getPublicSellerReviews: (sellerId, params = {}) =>
    apiClient.get(`/sellers/${sellerId}/reviews`, params),
};

// Product Q&A API Service (matching backend: handlers/questions.go)
export const questionsAPI = {
  // Get questions for a product — backend: GET /products/:id/questions
  getProductQuestions: (productId, params = {}) =>
    apiClient.get(`/products/${productId}/questions`, params),

  // Create a question on a product — backend: POST /questions/
  createQuestion: (productId, question) =>
    apiClient.post('/questions/', { product_id: productId, question }),

  // Answer a question (seller only) — backend: PUT /questions/:id/answer
  answerQuestion: (questionId, answer) =>
    apiClient.put(`/questions/${questionId}/answer`, { answer }),

};

// Order Tracking API Service (matching backend: handlers/tracking.go)
export const trackingAPI = {
  // Get order tracking information — backend: GET /orders/:id/track
  getOrderTracking: (orderId) => apiClient.get(`/orders/${orderId}/track`),

  // Add tracking event (seller only) — backend: POST /seller/tracking
  addTrackingEvent: (data) => apiClient.post('/seller/tracking', {
    order_id: data.orderId,
    status: data.status,
    location: data.location || '',
    description: data.description,
    is_public: data.isPublic !== false
  })
};

// Disputes API Service (matching mobile app implementation)
// Disputes are tickets of type "dispute" in the unified ticket system — there
// is no separate /disputes service on the backend, so every call here goes
// through /tickets (user-facing) or /admin/disputes (moderation).
export const disputesAPI = {
  // Get user's disputes — filtered client-side from their tickets
  getDisputes: async () => {
    const res = await apiClient.get('/tickets/');
    const tickets = res?.data?.tickets || res?.data || [];
    const disputes = Array.isArray(tickets) ? tickets.filter(t => t.type === 'dispute') : [];
    return { ...res, data: disputes };
  },

  // Get specific dispute
  getDispute: (disputeId) => apiClient.get(`/tickets/${disputeId}`),

  // Create new dispute
  createDispute: (data) => apiClient.post('/tickets/', {
    type: 'dispute',
    subject: data.reason || 'Order dispute',
    message: data.description,
    category: data.reason || 'dispute',
    order_id: data.orderId,
    product_id: data.productId,
  }),

  // Get all disputes (admin only)
  getAllDisputes: (status = '', priority = '') => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    const qs = params.toString();
    return apiClient.get(`/admin/disputes${qs ? `?${qs}` : ''}`);
  },

  // Resolve dispute (admin only)
  resolveDispute: (disputeId, data) => apiClient.post(`/admin/disputes/${disputeId}/resolve`, {
    resolution: data.resolution,
    admin_notes: data.adminNotes || '',
    refund_amount: data.refundAmount || 0
  }),

  // Add message to dispute
  addDisputeMessage: (disputeId, message) => apiClient.post(`/tickets/${disputeId}/messages`, {
    message
  }),

  // Update dispute status (admin only)
  updateDisputeStatus: (disputeId, status) => apiClient.put(`/admin/disputes/${disputeId}/status`, {
    status
  })
};

// Support API Service (matching mobile app implementation)
export const supportAPI = {
  // Get support tickets
  getSupportTickets: () => apiClient.get('/support/tickets'),

  // Get specific support ticket
  getSupportTicket: (ticketId) => apiClient.get(`/support/tickets/${ticketId}`),

  // Create support ticket (matching mobile app format exactly)
  createSupportTicket: (ticketData) => apiClient.post('/support/tickets', ticketData),

  // Add message to support ticket
  addSupportMessage: (ticketId, message) => apiClient.post(`/support/tickets/${ticketId}/messages`, {
    message
  }),

  // Close support ticket (uses PUT method as per backend routes)
  closeSupportTicket: (ticketId) => apiClient.put(`/support/tickets/${ticketId}/close`),

  // VIP Support - Uses same endpoints as regular support but with priority flag
  createVIPTicket: (data) => apiClient.post('/support/tickets', {
    type: 'support',
    subject: data.subject,
    message: data.message,
    priority: data.priority || 'high',
    category: data.category || 'vip_support',
    is_vip: true
  }),

  getVIPTickets: () => apiClient.get('/support/tickets?is_vip=true'),

  getVIPTicket: (ticketId) => apiClient.get(`/support/tickets/${ticketId}`),

  startVIPChat: (data) => apiClient.post('/support/tickets', {
    type: 'support',
    subject: data.subject || 'VIP Chat Support',
    message: data.message,
    priority: 'high',
    category: 'vip_chat',
    is_vip: true
  }),


};

// Utility function to handle API responses with loading states
export const withLoadingState = async (apiCall, setLoading) => {
  try {
    setLoading(true);
    const result = await apiCall();
    return result;
  } catch (error) {
    throw error;
  } finally {
    setLoading(false);
  }
};

// KYC Verification API Service
// Verification API (matching backend: handlers/verification.go)
export const verificationAPI = {
  // Get comprehensive verification status
  getStatus: () => apiClient.get('/user/verification/status'),

  // Submit complete verification (email, NIN, address, documents)
  // Supports both JSON object and FormData
  submitVerification: (data) => {
    const isMultipart = data instanceof FormData;
    return apiClient.request('/user/verification/submit', {
      method: 'POST',
      body: isMultipart ? data : JSON.stringify(data),
      headers: isMultipart 
        ? { ...apiClient.getAuthHeaders() } 
        : { ...apiClient.getAuthHeaders(), 'Content-Type': 'application/json' }
    });
  },

  // Lookup identity details from ID number
  lookupIdentity: (idType, idNumber) => {
    const formData = new FormData();
    formData.append('id_type', idType);
    formData.append('id_number', idNumber);
    return apiClient.request('/user/verification/lookup', {
      method: 'POST',
      body: formData,
      headers: { ...apiClient.getAuthHeaders() }
    });
  },

  // Resend verification email
  resendVerificationEmail: (email) => apiClient.post('/auth/resend-email-verification', { email }),

  // Confirm an email-verification token (backend: GET /auth/verify-email?token=)
  verifyEmailToken: (token) => apiClient.get(`/auth/verify-email?token=${encodeURIComponent(token)}`),
};

// Legacy KYC API (deprecated - use verificationAPI instead)
export const kycAPI = {
  // Get KYC verification status
  getStatus: () => verificationAPI.getStatus(),

  // Email Verification
  sendEmailVerification: (email) => verificationAPI.resendVerificationEmail(email),
  // Email verification is handled by the auth service (token-based), not a
  // separate KYC endpoint — /kyc/email/verify was never registered.
  verifyEmail: (token) => verificationAPI.verifyEmailToken(token),
  resendEmailVerification: (email) => verificationAPI.resendVerificationEmail(email),

  // Identity Verification
  lookupIdentity: (idType, idNumber) => verificationAPI.lookupIdentity(idType, idNumber),
  submitIdentity: (formData) => verificationAPI.submitVerification(formData),

  // Business Verification (for sellers) — submits the "cac" document upload
  // (business_type/registration_number/tax_id + file) through the same
  // generic verification/submit flow as NIN/identity/address.
  submitBusiness: (formData) => verificationAPI.submitVerification(formData),

  // Address Verification
  submitAddress: (formData) => verificationAPI.submitVerification(formData),


};

// Swap Deals API Service
// Backend routes (all require premium): POST /swap/create, GET /swap/, GET /swap/my-products,
// GET /swap/:id, PUT /swap/:id/accept, PUT /swap/:id/reject, PUT /swap/:id/ship,
// PUT /swap/:id/deliver, PUT /swap/:id/complete, PUT /swap/:id/cancel
export const swapAPI = {
  // ===== LEGACY SWAP DEAL METHODS =====
  // Get all swap deals (both sent and received — filter client-side by initiator_id/recipient_id)
  getSwapDeals: () => apiClient.get('/swap'),

  // Create a swap deal — requires recipient_id, initiator_product_id, recipient_product_id
  createSwapDeal: (swapData) => apiClient.post('/swap/create', swapData),

  // Get specific swap deal
  getSwapDeal: (swapId) => apiClient.get(`/swap/${swapId}`),

  // Accept swap deal (Premium Seller only)
  acceptSwapDeal: (swapId) => apiClient.put(`/swap/${swapId}/accept`),

  // Reject swap deal
  rejectSwapDeal: (swapId, reason) => apiClient.put(`/swap/${swapId}/reject`, { reason }),

  // Confirm shipment (for shipping exchange method)
  confirmShipment: (swapId, trackingData) => apiClient.put(`/swap/${swapId}/ship`, trackingData),

  // Confirm delivery
  confirmDelivery: (swapId, isInitiator) => apiClient.put(`/swap/${swapId}/deliver`, { is_initiator: isInitiator }),

  // Complete swap deal
  completeSwapDeal: (swapId) => apiClient.put(`/swap/${swapId}/complete`),

  // Cancel swap deal (PUT not DELETE)
  cancelSwapDeal: (swapId, reason) => apiClient.put(`/swap/${swapId}/cancel`, { reason }),

  // Get user's products available for swap (seller's own swap-enabled products)
  getMySwapProducts: () => apiClient.get('/swap/my-products'),

  // ===== NEW SWAP FLOW API =====
  // Step 1: Submit swap offer
  submitOffer: (offerData) => apiClient.post('/swap/offer', offerData),

  // Get all user swap offers (buyer & seller sides)
  getSwaps: () => apiClient.get('/swap/offers'),

  // Get single swap offer details
  getSwap: (swapId) => apiClient.get(`/swap/${swapId}`),

  // Step 3B: Seller responds with top-up price
  respondToOffer: (swapId, topupAmount) =>
    apiClient.post(`/swap/${swapId}/respond`, { topup_amount: topupAmount }),

  // Seller submits IMEI for their OWN device, after accepting/responding to
  // the offer above. Write-once — never returned back; the initiator never
  // sees it at all.
  submitSellerIMEI: (swapId, imei1, imei2) =>
    apiClient.post(`/swap/${swapId}/seller-imei`, { imei1, imei2 }),

  // Step 3A: Seller declines offer
  declineOffer: (swapId) =>
    apiClient.post(`/swap/${swapId}/decline-offer`),

  // Step 5B: Initiator accepts price
  acceptPrice: (swapId) =>
    apiClient.post(`/swap/${swapId}/accept-price`),

  // Step 5A: Initiator declines price
  declinePrice: (swapId) =>
    apiClient.post(`/swap/${swapId}/decline-price`),

  // Step 6: Process payment
  processPayment: (swapId, paymentData) =>
    apiClient.post(`/swap/${swapId}/payment`, paymentData),

  // Cancel swap
  cancelSwap: (swapId) =>
    apiClient.put(`/swap/${swapId}/cancel`),
};

// Support & Help API Service

// Payment Methods API Service — backend: handlers/payment_methods.go
export const paymentMethodsAPI = {
  // Get saved payment methods — backend: GET /payment/methods
  getPaymentMethods: () => apiClient.get('/payment/methods'),
  getMethods: () => apiClient.get('/payment/methods'),

  // Add payment method — backend: POST /payment/methods
  addMethod: (methodData) => apiClient.post('/payment/methods', methodData),

  // Delete payment method — backend: DELETE /payment/methods/:id
  deleteMethod: (methodId) => apiClient.delete(`/payment/methods/${methodId}`),

  // Get payment history — backend: GET /payment/history
  getHistory: () => apiClient.get('/payment/history'),
};

// Search API Service
export const searchAPI = {
  // Basic product search
  searchProducts: (query, filters = {}) => {
    const params = { q: query, ...filters };
    return apiClient.get('/search', { params });
  },

  // Advanced search with filters
  advancedSearch: (searchData) => apiClient.post('/search/advanced', searchData),

  // Get search suggestions/autocomplete
  getSearchSuggestions: (query) => apiClient.get('/search/suggestions', { params: { q: query } }),
};

// Saved Searches API Service
export const savedSearchesAPI = {
  // Get saved searches
  getSavedSearches: () => apiClient.get('/saved-searches/'),

  // Create saved search
  createSavedSearch: (searchData) => apiClient.post('/saved-searches/', searchData),

  // Update saved search
  updateSavedSearch: (searchId, searchData) => apiClient.put(`/saved-searches/${searchId}`, searchData),

  // Delete saved search
  deleteSavedSearch: (searchId) => apiClient.delete(`/saved-searches/${searchId}`),

};

// Follow System API Service
export const followAPI = {
  // Follow user/seller
  followUser: (userId) => apiClient.post(`/follow/${userId}`),

  // Unfollow user/seller
  unfollowUser: (userId) => apiClient.delete(`/follow/${userId}`),

  // Get followers
  getFollowers: () => apiClient.get('/follow/followers'),

  // Get following
  getFollowing: () => apiClient.get('/follow/following'),

  // Check if following — calls GET /follow/following and scans the list for userId
  isFollowing: async (userId) => {
    try {
      const res = await apiClient.get('/follow/following');
      const list = res?.data?.following || [];
      const found = list.some(
        (entry) => (entry.user?.id || entry.user?._id || entry.followed_id) === userId
      );
      return { data: { is_following: found } };
    } catch {
      return { data: { is_following: false } };
    }
  },

  // Get a seller's public profile (includes total_followers)
  getSellerProfile: (sellerId) => apiClient.get(`/sellers/${sellerId}`),

};

// Price Alerts API Service
export const priceAlertsAPI = {
  // Get price alerts
  getPriceAlerts: () => apiClient.get('/price-alerts/'),

  // Create price alert
  createPriceAlert: (productId, targetPrice) =>
    apiClient.post('/price-alerts/', { product_id: productId, target_price: targetPrice }),

  // Update price alert
  updatePriceAlert: (alertId, targetPrice) =>
    apiClient.put(`/price-alerts/${alertId}`, { target_price: targetPrice }),

  // Delete price alert
  deletePriceAlert: (alertId) => apiClient.delete(`/price-alerts/${alertId}`),

};

// ===== PRODUCT VARIANTS API =====
export const variantsAPI = {
  // Add product variant
  addVariant: (productId, variantData) =>
    apiClient.post(`/products/${productId}/variants`, variantData),

  // Update product variant
  updateVariant: (productId, variantId, variantData) =>
    apiClient.put(`/products/${productId}/variants/${variantId}`, variantData),

  // Delete product variant
  deleteVariant: (productId, variantId) =>
    apiClient.delete(`/products/${productId}/variants/${variantId}`),

  // Get product variants
  getVariants: (productId) =>
    apiClient.get(`/products/${productId}/variants`),
};

// ===== BULK OPERATIONS API =====
export const bulkAPI = {
  // Bulk update product prices
  updatePrices: (productIds, priceAdjustment, adjustmentType = 'percentage') =>
    apiClient.post('/seller/bulk/update-prices', {
      product_ids: productIds,
      price_adjustment: priceAdjustment,
      adjustment_type: adjustmentType, // 'percentage' or 'fixed'
    }),

  // Bulk update product status
  updateStatus: (productIds, status) =>
    apiClient.post('/seller/bulk/update-status', {
      product_ids: productIds,
      status,
    }),

  // Export products to CSV
  exportProducts: () =>
    apiClient.get('/seller/bulk/export-products'),

  // Export orders to CSV
  exportOrders: () =>
    apiClient.get('/seller/bulk/export-orders'),

  // Import products from CSV
  importProducts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/seller/bulk/import-products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

};

// ===== TWO-FACTOR AUTHENTICATION API =====
export const twoFactorAPI = {
  // Enable 2FA
  enable: () =>
    apiClient.post('/user/2fa/enable'),

  // Verify 2FA setup with OTP
  verify: (otp) =>
    apiClient.post('/user/2fa/verify', { otp }),

  // Disable 2FA
  disable: (password) =>
    apiClient.post('/user/2fa/disable', { password }),

  // Get 2FA status
  getStatus: () =>
    apiClient.get('/user/2fa/status'),

  // Generate backup codes
  generateBackupCodes: () =>
    apiClient.post('/user/2fa/backup-codes'),

  // Verify login with 2FA (backend route is /auth/verify-2fa)
  verifyLogin: (otp) =>
    apiClient.post('/auth/verify-2fa', { otp }),
};

// ===== RECOMMENDATIONS API =====
export const recommendationsAPI = {
  // Get personalized recommendations — backend: GET /recommendations/personalized
  getPersonalized: (limit = 10) =>
    apiClient.get('/recommendations/personalized', { limit }),

  // Get trending products — backend: GET /recommendations/trending
  getTrending: (limit = 10) =>
    apiClient.get('/recommendations/trending', { limit }),

  // Get frequently bought together — backend: GET /products/:id/frequently-bought
  getFrequentlyBought: (productId) =>
    apiClient.get(`/products/${productId}/frequently-bought`),

  // Get similar products — backend: GET /products/:id/similar
  getSimilar: (productId, limit = 10) =>
    apiClient.get(`/products/${productId}/similar`, { limit }),

  // Get checkout recommendations — backend: GET /products/checkout-recommendations?product_id=<id>
  getCheckoutRecommendations: (productId) =>
    apiClient.get(`/products/checkout-recommendations${productId ? `?product_id=${productId}` : ''}`),
};

// ===== ADMIN API (Comprehensive Admin Operations) =====
// Matches backend routes from AutoboyExpress-backend/routes/routes.go lines 398-428
// and handlers from AutoboyExpress-backend/handlers/admin.go & admin_products.go
export const adminAPI = {
  // ===== DASHBOARD & ANALYTICS =====
  // Backend: GET /admin/dashboard (admin.go:26-104)
  getDashboard: () => apiClient.get('/admin/dashboard'),
  // Backend: GET /admin/analytics (admin.go:367-381)
  getAnalytics: () => apiClient.get('/admin/analytics'),
  getSystemAnalytics: () => apiClient.get('/admin/analytics'),
  getSystemMetrics: () => apiClient.get('/admin/system/metrics'),

  // ===== USER MANAGEMENT =====
  // Backend: GET /admin/users (admin.go:107-161)
  getUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiClient.get(`/admin/users${queryString ? `?${queryString}` : ''}`);
  },
  adminCreateUser: (userData) => apiClient.post('/admin/users', userData),
  // Backend: GET /admin/users/:id (admin.go:278-293)
  getUserDetails: (userId) => apiClient.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => apiClient.put(`/admin/users/${userId}`, data),
  // Backend: PUT /admin/users/:id/status (admin.go:212-276)
  updateUserStatus: (userId, status, reason = '') =>
    apiClient.put(`/admin/users/${userId}/status`, { status, reason }),
  adminBanUser: (userId, reason) =>
    apiClient.post(`/admin/users/${userId}/ban`, { reason }),
  suspendUser: (userId, reason) =>
    apiClient.put(`/admin/users/${userId}/status`, { status: 'suspended', reason }),
  unsuspendUser: (userId) =>
    apiClient.put(`/admin/users/${userId}/status`, { status: 'active' }),
  deleteUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  // Backend: PUT /admin/users/:user_id/verification/approve (verificationHandler)
  approveUserVerification: (userId, notes = '') =>
    apiClient.put(`/admin/users/${userId}/verification/approve`, { notes }),
  // Backend: PUT /admin/users/:user_id/verification/reject (verificationHandler)
  rejectUserVerification: (userId, reason) =>
    apiClient.put(`/admin/users/${userId}/verification/reject`, { reason }),

  // ===== KYC VERIFICATION MANAGEMENT =====
  // These endpoints are handled through user verification endpoints above
  getKYCRequests: (status = 'pending') => apiClient.get(`/admin/kyc?status=${status}`),
  getKYCDetails: (userId) => apiClient.get(`/admin/kyc/${userId}`),
  // Backend: GET /admin/kyb (admin_kyc.go GetBusinessVerifications) — one row
  // per user with a submitted "cac" business document.
  getKYBRequests: () => apiClient.get('/admin/kyb'),
  approveKYC: (userId, notes = '', documentType = 'nin') =>
    apiClient.put(`/admin/users/${userId}/verification/approve`, { notes, document_type: documentType }),
  rejectKYC: (userId, reason, documentType = 'nin') =>
    apiClient.put(`/admin/users/${userId}/verification/reject`, { reason, document_type: documentType }),

  // ===== PRODUCT MANAGEMENT =====
  // Backend: GET /admin/products (admin.go:295-310)
  getAllProducts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiClient.get(`/admin/products${queryString ? `?${queryString}` : ''}`);
  },
  getProductDetails: (productId) => apiClient.get(`/admin/products/${productId}`),
  // Backend: PUT /admin/products/:id/approve (admin_products.go:90-122)
  approveProduct: (productId) => apiClient.put(`/admin/products/${productId}/approve`),
  // Backend: PUT /admin/products/:id/reject (admin_products.go:125-164)
  rejectProduct: (productId, reason) =>
    apiClient.put(`/admin/products/${productId}/reject`, { reason }),
  // Update product details (admin can update any product)
  updateProduct: (productId, productData) => apiClient.put(`/admin/products/${productId}`, productData),
  // Update product status via flags endpoint
  updateProductStatus: (productId, status) =>
    apiClient.put(`/admin/products/${productId}/flags`, { status }),
  deleteProduct: (productId) => apiClient.delete(`/admin/products/${productId}`),
  // Backend: PUT /admin/products/:id/flags (admin_products.go:33-87)
  updateProductFlags: (productId, flags) =>
    apiClient.put(`/admin/products/${productId}/flags`, flags),
  featureProduct: (productId) =>
    apiClient.put(`/admin/products/${productId}/flags`, { is_featured: true }),
  unfeatureProduct: (productId) =>
    apiClient.put(`/admin/products/${productId}/flags`, { is_featured: false }),
  // Admin can create products (uses general product endpoint with admin auth)
  // Backend: POST /admin/products (admin_products.go)
  createProduct: (productData) => apiClient.post('/admin/products', productData),

  // ===== ORDER MANAGEMENT =====
  // Backend: GET /admin/orders (admin.go:164-209)
  getAllOrders: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiClient.get(`/admin/orders${queryString ? `?${queryString}` : ''}`);
  },
  // Backend: GET /admin/orders/:id (admin.go:350-365)
  getOrderDetails: (orderId) => apiClient.get(`/admin/orders/${orderId}`),
  updateOrderStatus: (orderId, status) =>
    apiClient.put(`/admin/orders/${orderId}/status`, { status }),
  cancelOrder: (orderId, reason) =>
    apiClient.put(`/admin/orders/${orderId}/status`, { status: "cancelled", reason }),
  updateOrderSpecialPackaging: (orderId, special_packaging) =>
    apiClient.put(`/admin/orders/${orderId}/packaging`, { special_packaging }),
  updateOrderPerks: (orderId, perks) =>
    apiClient.put(`/admin/orders/${orderId}/perks`, { perks }),

  /* ESCROW SERVICE DISABLED
  getAllEscrows: (params = {}) => apiClient.get('/admin/escrows', params),
  getEscrowDetails: (escrowId) => apiClient.get(`/admin/escrows/${escrowId}`),
  releaseEscrow: (escrowId, notes = '') =>
    apiClient.post(`/admin/escrows/${escrowId}/release`, { notes }),
  refundEscrow: (escrowId, reason) =>
    apiClient.post(`/admin/escrows/${escrowId}/refund`, { reason }),
  getEscrowAnalytics: () => apiClient.get('/admin/escrows/analytics'),
  getEscrowSettings: () => apiClient.get('/admin/escrows/settings'),
  updateEscrowSettings: (settings) => apiClient.put('/admin/escrows/settings', settings),
  */

  // ===== DISPUTE MANAGEMENT =====
  getAllDisputes: (params = {}) => apiClient.get('/admin/disputes', params),
  getDisputeDetails: (disputeId) => apiClient.get(`/admin/disputes/${disputeId}`),
  resolveDispute: (disputeId, resolution) =>
    apiClient.post(`/admin/disputes/${disputeId}/resolve`, resolution),
  updateDisputeStatus: (disputeId, status) =>
    apiClient.put(`/admin/disputes/${disputeId}/status`, { status }),
  addDisputeNote: (disputeId, note) =>
    apiClient.post(`/admin/disputes/${disputeId}/notes`, { note }),

  // ===== TRANSACTION MANAGEMENT =====
  getAllTransactions: (params = {}) => apiClient.get('/admin/transactions', params),
  getTransactionDetails: (transactionId) =>
    apiClient.get(`/admin/transactions/${transactionId}`),
  refundTransaction: (transactionId, reason) =>
    apiClient.post(`/admin/transactions/${transactionId}/refund`, { reason }),

  // ===== WALLET MANAGEMENT =====
  getUserWallets: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiClient.get(`/admin/wallets${queryString ? `?${queryString}` : ''}`);
  },
  getWalletDetails: (userId) => apiClient.get(`/admin/wallets/${userId}`),
  adjustWallet: (userId, amount, reason, type = 'credit') =>
    apiClient.post(`/admin/wallets/${userId}/adjust`, { amount, reason, type }),
  freezeWallet: (userId, reason) =>
    apiClient.post(`/admin/wallets/${userId}/freeze`, { reason }),
  unfreezeWallet: (userId) =>
    apiClient.post(`/admin/wallets/${userId}/unfreeze`),

  // ===== MODERATION & REPORTS =====
  getReports: (params = {}) => apiClient.get('/admin/reports', params),
  updateReportStatus: (reportId, status) =>
    apiClient.put(`/admin/reports/${reportId}/status`, { status }),
  // The backend records `reason` on the resulting warning/suspension, so send
  // it under that key (it also accepts `notes` as an alias).
  takeReportAction: (reportId, action, reason = '') =>
    apiClient.post(`/admin/reports/${reportId}/action`, { action, reason, notes: reason }),
  getProhibitedItems: () => apiClient.get('/admin/moderation/prohibited-items'),
  updateProhibitedItems: (items) => apiClient.put('/admin/moderation/prohibited-items', items),

  // ===== REVIEW MANAGEMENT =====
  getAllReviews: (params = {}) => apiClient.get('/admin/reviews', params),
  approveReview: (reviewId) => apiClient.post(`/admin/reviews/${reviewId}/approve`),
  rejectReview: (reviewId, reason) =>
    apiClient.post(`/admin/reviews/${reviewId}/reject`, { reason }),
  deleteReview: (reviewId) => apiClient.delete(`/admin/reviews/${reviewId}`),

  // ===== ADMIN USER MANAGEMENT =====
  getAdmins: () => apiClient.get('/admin/admins'),
  createAdmin: (data) => apiClient.post('/admin/admins', data),
  updateAdminRole: (adminId, role) =>
    apiClient.put(`/admin/admins/${adminId}/role`, { role }),
  deactivateAdmin: (adminId) =>
    apiClient.post(`/admin/admins/${adminId}/deactivate`),

  // ===== PREMIUM/SUBSCRIPTION MANAGEMENT — backend: /admin/premium-users =====
  getPremiumUsers: () => apiClient.get('/admin/premium-users'),
  grantPremium: (userId, planId, durationDays, reason = '') =>
    apiClient.post(`/admin/premium-users/${userId}/grant`, { plan_id: planId, duration_days: durationDays, reason }),
  revokePremium: (userId, reason) =>
    apiClient.post(`/admin/premium-users/${userId}/revoke`, { reason }),
  extendPremium: (userId, days) =>
    apiClient.post(`/admin/premium-users/${userId}/extend`, { extension_days: days }),
  getPremiumAnalytics: () => apiClient.get('/admin/premium-users/analytics'),
  getPremiumSettings: () => apiClient.get('/admin/premium-settings'),
  updatePremiumSettings: (settings) => apiClient.put('/admin/premium-settings', settings),
  updateUserPerks: (userId, perks) => apiClient.put(`/admin/users/${userId}/perks`, { perks }),
  verifyUserBadge: (userId, verified, reason) => apiClient.put(`/admin/users/${userId}/verify-badge`, { verified, reason }),

  // ===== CATEGORY MANAGEMENT =====
  getCategories: () => apiClient.get('/categories'),
  createCategory: (data) => apiClient.post('/admin/categories', data),
  updateCategory: (categoryId, data) =>
    apiClient.put(`/admin/categories/${categoryId}`, data),
  deleteCategory: (categoryId) =>
    apiClient.delete(`/admin/categories/${categoryId}`),
  reorderCategories: (order) =>
    apiClient.post('/admin/categories/reorder', { order }),

  // ===== NOTIFICATION MANAGEMENT =====
  sendBulkNotification: (data) =>
    apiClient.post('/admin/notifications/bulk', data),
  getNotificationLogs: (params = {}) =>
    apiClient.get('/admin/notifications/logs', params),
  getNotificationTemplates: () => apiClient.get('/admin/notifications/templates'),
  createNotificationTemplate: (data) => apiClient.post('/admin/notifications/templates', data),
  getNotificationSchedules: () => apiClient.get('/admin/notifications/schedule'),
  createNotificationSchedule: (data) => apiClient.post('/admin/notifications/schedule', data),
  deleteNotificationSchedule: (scheduleId) => apiClient.delete(`/admin/notifications/schedule/${scheduleId}`),
  getNotificationStats: () => apiClient.get('/admin/notifications/stats'),

  // ===== WALLET / WITHDRAWAL MANAGEMENT =====
  getWalletWithdrawals: (params = {}) => apiClient.get('/admin/withdrawals', params),
  approveWithdrawal: (withdrawalId) => apiClient.post(`/admin/withdrawals/${withdrawalId}/approve`),
  rejectWithdrawal: (withdrawalId, reason) =>
    apiClient.post(`/admin/withdrawals/${withdrawalId}/reject`, { reason }),

  // ===== KYC BULK ACTIONS =====
  bulkApproveKYC: (kycIds) => apiClient.post('/admin/kyc/bulk-approve', { kyc_ids: kycIds }),
  bulkRejectKYC: (kycIds, reason) =>
    apiClient.post('/admin/kyc/bulk-reject', { kyc_ids: kycIds, reason }),

  // ===== ADMIN TICKET MANAGEMENT — backend: /admin/tickets =====
  getAdminTickets: (params = {}) => apiClient.get('/admin/tickets', params),
  getAdminTicket: (ticketId) => apiClient.get(`/admin/tickets/${ticketId}`),
  updateAdminTicketStatus: (ticketId, status) => apiClient.put(`/admin/tickets/${ticketId}/status`, { status }),
  replyAdminTicket: (ticketId, message) => apiClient.post(`/admin/tickets/${ticketId}/reply`, { message }),
  getAdminTicketStats: () => apiClient.get('/admin/tickets/stats'),

  // ===== MARKETING & PROMOTIONS (Banners, Flash Sales, Promo Codes) =====
  // Kept as separate aliases (used by AdminDashboard's initial data fetch) —
  // create/update/delete/toggle for these live once, further below, since
  // they used to be defined twice in this same object (the later definition
  // always won at runtime, but relying on that silently was fragile: the two
  // togglePromoStatus definitions actually disagreed on payload shape).
  getAllBanners: () => apiClient.get('/admin/marketing/banners'),
  getAllFlashSales: () => apiClient.get('/admin/marketing/flash-sales'),
  getAllPromoCodes: () => apiClient.get('/admin/marketing/promo-codes'),

  // ===== SWAP DEVICE INSPECTION =====
  inspectSwapDevice: (swapId, inspectData) => apiClient.post(`/admin/swap/${swapId}/inspect`, inspectData),

  // ===== IMEI INSPECTION (admin-only — the one place these are ever returned) =====
  getOrderIMEI: (orderId) => apiClient.get(`/admin/orders/${orderId}/imei`),
  getSwapIMEI: (swapId) => apiClient.get(`/admin/swaps/${swapId}/imei`),

  // ===== DEVICE COLOR MAPPING (model -> valid colors) =====
  getDeviceColorMappings: () => apiClient.get('/admin/device-colors'),
  createDeviceColorMapping: (data) => apiClient.post('/admin/device-colors', data),
  updateDeviceColorMapping: (id, data) => apiClient.put(`/admin/device-colors/${id}`, data),
  deleteDeviceColorMapping: (id) => apiClient.delete(`/admin/device-colors/${id}`),

  // ===== SYSTEM MONITORING =====
  getSystemMetricsDetailed: () => apiClient.get('/admin/system/metrics'),
  getSystemLogs: (params = {}) => apiClient.get('/admin/system/logs', params),
  getAPIStats: () => apiClient.get('/admin/system/api-stats'),
  getDatabaseStats: () => apiClient.get('/admin/system/database-stats'),
  getRecentActivity: (params = {}) => apiClient.get('/admin/system/activity', params),
  exportSystemData: (params = {}) => apiClient.get('/admin/system/export', params),

  // ===== ADVANCED ANALYTICS =====
  getUserAnalytics: () => apiClient.get('/admin/analytics/users'),
  getRevenueAnalytics: () => apiClient.get('/admin/analytics/revenue'),
  getTopCategories: () => apiClient.get('/admin/analytics/top-categories'),
  getTopSellers: () => apiClient.get('/admin/analytics/top-sellers'),
  exportAnalytics: (params = {}) => apiClient.get('/admin/analytics/export', params),

  // ===== TRANSACTION & FINANCIAL EXTRAS =====
  exportTransactions: (params = {}) => apiClient.get('/admin/transactions/export', params),
  getTransactionFees: () => apiClient.get('/admin/transactions/fees'),
  getFinancialSettlementSummary: () => apiClient.get('/admin/financial/settlement-summary'),
  getCommissionTiers: () => apiClient.get('/admin/financial/commission-tiers'),
  createCommissionTier: (data) => apiClient.post('/admin/financial/commission-tiers', data),
  updateCommissionTier: (tierId, data) => apiClient.put(`/admin/financial/commission-tiers/${tierId}`, data),
  deleteCommissionTier: (tierId) => apiClient.delete(`/admin/financial/commission-tiers/${tierId}`),
  getCommissionSettings: () => apiClient.get('/admin/financial/commission-settings'),
  updateCommissionSettings: (data) => apiClient.put('/admin/financial/commission-settings', data),

  // ===== ADMIN ACTIVITY =====
  getAdminActivityLogs: () => apiClient.get('/admin/admins/activity-logs'),

  // ===== REVIEW EXTRAS =====
  flagReview: (reviewId) => apiClient.post(`/admin/reviews/${reviewId}/flag`),

  // ===== CATEGORY EXTRAS =====
  getCategoryStats: (categoryId) => apiClient.get(`/admin/categories/${categoryId}/stats`),

  // ===== NOTIFICATION MANAGEMENT =====
  createNotificationTemplate: (data) => apiClient.post('/admin/notifications/templates', data),
  getNotificationTemplates: () => apiClient.get('/admin/notifications/templates'),
  getNotificationStats: () => apiClient.get('/admin/notifications/stats'),
  scheduleNotification: (data) => apiClient.post('/admin/notifications/schedule', data),
  getScheduledNotifications: () => apiClient.get('/admin/notifications/scheduled'),
  cancelScheduledNotification: (id) => apiClient.delete(`/admin/notifications/scheduled/${id}`),

  // ===== SWAP MANAGEMENT =====
  getAllSwaps: (params = {}) => apiClient.get('/admin/swaps', params),
  getPendingSwaps: () => apiClient.get('/admin/swaps/pending'),
  approveSwap: (swapId) => apiClient.post(`/admin/swaps/${swapId}/approve`),
  rejectSwap: (swapId, reason) => apiClient.post(`/admin/swaps/${swapId}/reject`, { reason }),

  // ===== CMS EXTRAS =====
  createBlogPost: (data) => apiClient.post('/admin/cms/blog-posts', data),
  updateBlogPost: (id, data) => apiClient.put(`/admin/cms/blog-posts/${id}`, data),
  deleteBlogPost: (id) => apiClient.delete(`/admin/cms/blog-posts/${id}`),
  getBlogPosts: () => apiClient.get('/admin/cms/blog-posts'),
  getMediaLibrary: () => apiClient.get('/admin/cms/media'),
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.request('/admin/cms/media', {
      method: 'POST',
      body: formData,
      headers: { ...apiClient.getAuthHeaders() },
    });
  },
  deleteMedia: (id) => apiClient.delete(`/admin/cms/media/${id}`),

  // ===== SYSTEM SETTINGS =====
  getSettings: () => apiClient.get('/admin/settings'),
  updateGeneralSettings: (settings) => apiClient.put('/admin/settings/general', settings),
  updatePaymentSettings: (settings) => apiClient.put('/admin/settings/payment', settings),
  updateSecuritySettings: (settings) => apiClient.put('/admin/settings/security', settings),
  updateLogisticsSettings: (settings) => apiClient.put('/admin/settings/logistics', settings),
  toggleMaintenanceMode: (enabled) => apiClient.post('/admin/settings/maintenance', { enabled }),
  // Legacy alias
  updateSettings: (settings) => apiClient.put('/admin/settings/general', settings),
  // Backend: GET /admin/system/status
  getSystemStatus: () => apiClient.get('/admin/system/status'),
  // Backend: POST /admin/system/init-database
  initializeDatabase: () => apiClient.post('/admin/system/init-database'),
  // Backend: GET /admin/system/test-endpoints
  testEndpoints: () => apiClient.get('/admin/system/test-endpoints'),

  // ===== CMS/CONTENT MANAGEMENT =====
  // Banners — backend: /admin/marketing/banners
  getBanners: () => apiClient.get('/admin/marketing/banners'),
  createBanner: (data) => apiClient.post('/admin/marketing/banners', data),
  updateBanner: (bannerId, data) =>
    apiClient.put(`/admin/marketing/banners/${bannerId}`, data),
  deleteBanner: (bannerId) =>
    apiClient.delete(`/admin/marketing/banners/${bannerId}`),
  // Flash Sales — backend: /admin/marketing/flash-sales
  getFlashSales: () => apiClient.get('/admin/marketing/flash-sales'),
  createFlashSale: (data) => apiClient.post('/admin/marketing/flash-sales', data),
  updateFlashSale: (id, data) => apiClient.put(`/admin/marketing/flash-sales/${id}`, data),
  deleteFlashSale: (id) => apiClient.delete(`/admin/marketing/flash-sales/${id}`),
  // Promo Codes — backend: /admin/marketing/promo-codes
  getPromoCodes: () => apiClient.get('/admin/marketing/promo-codes'),
  createPromoCode: (data) => apiClient.post('/admin/marketing/promo-codes', data),
  updatePromoCode: (id, data) => apiClient.put(`/admin/marketing/promo-codes/${id}`, data),
  deletePromoCode: (id) => apiClient.delete(`/admin/marketing/promo-codes/${id}`),
  togglePromoStatus: (id, is_active) => apiClient.put(`/admin/marketing/promo-codes/${id}/status`, { is_active }),
  // Pages — backend: /admin/cms/pages
  getPages: () => apiClient.get('/admin/cms/pages'),
  createOrUpdatePage: (pageData) => apiClient.post('/admin/cms/pages', pageData),
  updatePage: (pageId, content) =>
    apiClient.put(`/admin/cms/pages/${pageId}`, { content }),
  deletePage: (pageId) => apiClient.delete(`/admin/cms/pages/${pageId}`),

  // ===== SYSTEM LOGS =====
  getAuditLogs: (params = {}) => apiClient.get('/admin/logs/audit', params),
  getErrorLogs: (params = {}) => apiClient.get('/admin/logs/errors', params),

  // ===== STATISTICS & REPORTS =====
  getSalesReport: (startDate, endDate) =>
    apiClient.get(`/admin/reports/sales?start=${startDate}&end=${endDate}`),
  getUserReport: (startDate, endDate) =>
    apiClient.get(`/admin/reports/users?start=${startDate}&end=${endDate}`),
  getRevenueReport: (period = '30d') =>
    apiClient.get(`/admin/reports/revenue?period=${period}`),
  exportData: (type, format = 'csv') =>
    apiClient.get(`/admin/export/${type}?format=${format}`, { responseType: 'blob' }),
};

// Utility function to handle API errors with user feedback
export const handleAPIError = (error, showNotification = null) => {

  if (showNotification) {
    showNotification({
      type: 'error',
      message: error.message || 'An unexpected error occurred',
    });
  }

  return error;
};

// ===== HELPER FUNCTIONS =====

// Format date helper
export const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Format currency helper
export const formatCurrency = (amount, currency = 'NGN') => {
  if (amount === null || amount === undefined) return '₦0';
  return `₦${parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// Get status badge color helper
export const getStatusColor = (status) => {
  const statusColors = {
    // Order statuses
    pending: 'warning',
    confirmed: 'info',
    paid: 'info',
    processing: 'info',
    shipped: 'primary',
    delivered: 'success',
    completed: 'success',
    cancelled: 'danger',
    refunded: 'danger',
    disputed: 'danger',

    // User statuses
    active: 'success',
    inactive: 'warning',
    suspended: 'danger',
    banned: 'danger',

    // Payment statuses
    escrow: 'info',
    failed: 'danger',

    // Generic
    open: 'warning',
    closed: 'secondary',
    resolved: 'success',
  };

  return statusColors[status?.toLowerCase()] || 'secondary';
};

// Get payment status color helper
export const getPaymentStatusColor = (status) => {
  const statusColors = {
    pending: 'warning',
    paid: 'success',
    completed: 'success',
    escrow: 'info',
    failed: 'danger',
    refunded: 'danger',
    cancelled: 'danger',
  };

  return statusColors[status?.toLowerCase()] || 'secondary';
};

// Extract user name helper
export const extractUserName = (user) => {
  if (!user) return 'Unknown';

  if (user.profile) {
    const firstName = user.profile.first_name || user.profile.firstName || '';
    const lastName = user.profile.last_name || user.profile.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
  }

  return user.email || user.username || 'Unknown';
};

// ===== DATA MANAGEMENT API =====
export const dataManagementAPI = {
  // Get data storage info — backend: GET /user/data-storage
  getDataStorageInfo: () => apiClient.get('/user/data-storage'),

  // Clear user cache — backend: POST /user/clear-cache
  clearCache: () => apiClient.post('/user/clear-cache'),

  // Export all user data — backend: POST /user/export-data
  exportUserData: () => apiClient.post('/user/export-data'),

  // Delete all user data — backend: DELETE /user/delete-all-data
  deleteAllUserData: () => apiClient.delete('/user/delete-all-data'),
};

// ===== SECURITY API =====
export const securityAPI = {
  // Get activity/security log — backend: GET /user/activity-log
  getActivityLog: (params = {}) => apiClient.get('/user/activity-log', params),

  // Get active sessions — backend: GET /user/security/sessions
  getActiveSessions: () => apiClient.get('/user/security/sessions'),

  // Revoke a specific session — backend: DELETE /user/security/sessions/:id
  revokeSession: (sessionId) => apiClient.delete(`/user/security/sessions/${sessionId}`),

  // Revoke all other sessions — backend: DELETE /user/security/sessions/all
  revokeAllSessions: () => apiClient.delete('/user/security/sessions/all'),
};

// ===== LOGISTICS & DELIVERY API =====
export const logisticsAPI = {
  // Dispatch rider for delivery
  dispatchRider: (orderData) =>
    apiClient.post('/logistics/dispatch', orderData),

  // Track delivery
  trackDelivery: (trackingId) =>
    apiClient.get(`/logistics/track/${trackingId}`),

  // Get delivery status (alias for trackDelivery)
  getStatus: (trackingNumber) =>
    apiClient.get(`/logistics/track/${trackingNumber}`),

  // Cancel dispatch
  cancelDispatch: (trackingId) =>
    apiClient.post(`/logistics/dispatch/${trackingId}/cancel`),

  // Get delivery codes (pickup and delivery)
  getDeliveryCodes: (orderId) =>
    apiClient.get(`/user/orders/${orderId}/delivery-codes`),

  // Verify delivery code
  verifyDeliveryCode: (orderId, codeData) =>
    apiClient.post(`/orders/${orderId}/verify-delivery`, codeData),

  // Get rider information
  getRiderInfo: (trackingId) =>
    apiClient.get(`/logistics/rider/${trackingId}`),

  // Get driver info (alias for getRiderInfo)
  getDriver: (trackingNumber) =>
    apiClient.get(`/logistics/rider/${trackingNumber}`),

  // Update delivery status
  updateDeliveryStatus: (trackingId, status) =>
    apiClient.put(`/logistics/delivery/${trackingId}/status`, { status }),

  // Get delivery history for order
  getDeliveryHistory: (orderId) =>
    apiClient.get(`/orders/${orderId}/delivery-history`),

  // Get order tracking details
  getOrderTracking: (orderId) =>
    apiClient.get(`/logistics/order/${orderId}/tracking`),

  // Get delivery order details
  getDeliveryOrder: (trackingNumber) =>
    apiClient.get(`/logistics/orders/${trackingNumber}`),

  // Request delivery reschedule
  rescheduleDelivery: (trackingNumber, newDate) =>
    apiClient.post(`/logistics/track/${trackingNumber}/reschedule`, { new_date: newDate }),

  // Rate delivery
  rateDelivery: (trackingNumber, rating, comment) =>
    apiClient.post(`/logistics/rate/${trackingNumber}`, { rating, comment }),
};

// ===== SHIPPING API =====
export const shippingAPI = {
  // Calculate shipping cost
  calculateShipping: (shippingData) =>
    apiClient.post('/shipping/calculate', shippingData),

  // Get shipping quote
  getQuote: (address, items) =>
    apiClient.post('/shipping/calculate', { address, items }),

  // Track shipment
  trackShipment: (trackingNumber) =>
    apiClient.get(`/shipping/track/${trackingNumber}`),

  // Get delivery zones
  getZones: () => apiClient.get('/shipping/zones'),

  // Estimate delivery time
  estimateDelivery: (address) =>
    apiClient.get('/shipping/estimate', { address }),

  // Verify address
  verifyAddress: (address) =>
    apiClient.post('/shipping/verify-address', { address }),

  // Get shipping rates
  getRates: (params = {}) =>
    apiClient.get('/shipping/rates', params),
};

// ===== UPLOAD API =====
// General media upload — backend: POST /upload
export const uploadAPI = {
  // Upload a single file; returns { data: { url: "https://...", filename: "...", type: "..." } }
  uploadFile: (file, type = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    return apiClient.request('/upload', {
      method: 'POST',
      body: formData,
      headers: { ...apiClient.getAuthHeaders() }, // no Content-Type — browser sets multipart boundary
    });
  },
  // Legacy alias
  uploadImage: (file) => uploadAPI.uploadFile(file, 'image'),
};

// ===== DEALS API =====
export const dealsAPI = {
  // Get flash deals (public) — backend: GET /deals/flash-deals
  getFlashDeals: (params = {}) => apiClient.get('/deals/flash-deals', params),

  // Get exclusive deals (premium) — backend: GET /deals/exclusive
  getExclusiveDeals: (params = {}) => apiClient.get('/deals/exclusive', params),

  // Get priority listings — backend: GET /deals/priority-listings
  getPriorityListings: (params = {}) => apiClient.get('/deals/priority-listings', params),
};

export const settingsAPI = {
  // Admin-configurable VAT rate (Admin → Service Charges) — backend: GET /settings/vat-rate
  // Same commission_settings.vat_percentage value used to compute seller
  // commission, also applied to the buyer-facing Logistics VAT.
  getVatRate: () => apiClient.get('/settings/vat-rate'),

  // Buyer-facing checkout fee rates: VAT % (for Logistics VAT) and Escrow
  // Fee % (Admin → Settings → Escrow Fee) — backend: GET /settings/checkout-fee-rates
  getCheckoutFeeRates: () => apiClient.get('/settings/checkout-fee-rates'),
};

// ===== REQUESTS API (Buyer product requests) =====
export const requestsAPI = {
  // Create a product request — backend: POST /requests/
  createRequest: (data) => apiClient.post('/requests/', data),

  // Get all requests (public) — backend: GET /requests/
  getAllRequests: (params = {}) => apiClient.get('/requests/', params),

  // Get current user's requests — backend: GET /requests/my
  getMyRequests: () => apiClient.get('/requests/my'),

  // Get a specific request — backend: GET /requests/:id
  getRequest: (requestId) => apiClient.get(`/requests/${requestId}`),

  // Get responses to a request — backend: GET /requests/:id/responses
  getRequestResponses: (requestId) => apiClient.get(`/requests/${requestId}/responses`),

  // Seller responds to a request with a product URL — backend: POST /requests/:id/respond
  respondToRequest: (requestId, data) => apiClient.post(`/requests/${requestId}/respond`, data),

  // Buyer marks request as fulfilled — backend: PUT /requests/:id/fulfill
  fulfillRequest: (requestId) => apiClient.put(`/requests/${requestId}/fulfill`),

  // Delete a request — backend: DELETE /requests/:id
  deleteRequest: (requestId) => apiClient.delete(`/requests/${requestId}`),
};

// ===== DEVICE SUBMISSIONS API =====
export const deviceSubmissionsAPI = {
  // Create device submission — backend: POST /device-submissions/
  createSubmission: (data) => apiClient.post('/device-submissions/', data),

  // Get current user's submissions — backend: GET /device-submissions/my
  getMySubmissions: () => apiClient.get('/device-submissions/my'),

  // Get a specific submission — backend: GET /device-submissions/:id
  getSubmission: (submissionId) => apiClient.get(`/device-submissions/${submissionId}`),

  // Get submission by swap ID — backend: GET /device-submissions/swap/:swap_id
  getSubmissionBySwap: (swapId) => apiClient.get(`/device-submissions/swap/${swapId}`),
};

/* ESCROW SERVICE DISABLED
export const escrowSwapAPI = {
  createSwapTransaction: (data) => apiClient.post('/escrow/swap', data),
  verifyPickupCode: (swapId, code) =>
    apiClient.post(`/escrow/swap/${swapId}/verify-pickup`, { code }),
  verifyDeliveryCode: (swapId, code) =>
    apiClient.post(`/escrow/swap/${swapId}/verify-delivery`, { code }),
  dispatchRider: (swapId) =>
    apiClient.post(`/escrow/swap/${swapId}/dispatch-rider`),
  verifyCACBusiness: (data) => apiClient.post('/kyc/verify-business', data),
};
*/

// Stores / Premium Sellers API
export const storeAPI = {
  // Get premium sellers (top sellers with real-time data from backend)
  getPremiumSellers: (params = {}) => apiClient.get('/sellers/top', params),

  // Get a single store by UUID
  getStore: (storeId) => apiClient.get(`/stores/${storeId}`),

  // Get products listed by a specific store
  getStoreProducts: (storeId, params = {}) => apiClient.get(`/stores/${storeId}/products`, params),
};

// Promotions / Banners API (based on backend promo.go model)
export const promotionsAPI = {
  // Get currently active flash deals — backend: GET /deals/flash-deals (public)
  getActive: () => apiClient.get('/deals/flash-deals'),

  // Get active homepage banners created in Admin → Marketing — backend: GET /banners (public)
  // Returns only is_active banners (admin's GET /admin/marketing/banners returns all).
  getBanners: () => apiClient.get('/banners'),

  // ─── Admin Promo Code Management — backend: /admin/marketing/promo-codes ───
  // List all promo codes
  getAll: (params = {}) => apiClient.get('/admin/marketing/promo-codes', { params }),

  // Get single promo code by ID
  getById: (id) => apiClient.get(`/admin/marketing/promo-codes/${id}`),

  // Create new promo code
  create: (data) => apiClient.post('/admin/marketing/promo-codes', data),

  // Update existing promo code
  update: (id, data) => apiClient.put(`/admin/marketing/promo-codes/${id}`, data),

  // Delete promo code
  delete: (id) => apiClient.delete(`/admin/marketing/promo-codes/${id}`),

  // Toggle active status — backend: PUT /admin/marketing/promo-codes/:id/status
  toggleActive: (id, isActive) => apiClient.put(`/admin/marketing/promo-codes/${id}/status`, { is_active: isActive }),

  // Validate promo code — backend: POST /promo-codes/validate (public)
  validate: (code, orderAmount = 0) => apiClient.post('/promo-codes/validate', { code, order_amount: orderAmount }),
};

// Export API client for direct usage if needed
export default apiClient;
